import { Injectable, inject, PLATFORM_ID, effect } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { io, Socket } from 'socket.io-client';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket!: Socket;
  private authService = inject(AuthService);

  private notificationSubject = new Subject<any>();
  public notifications$ = this.notificationSubject.asObservable();

  constructor() {
    this.socket = io(environment.SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket'],
      autoConnect: false // Connect manually
    });

    if (!environment.production) {
      console.log("....Socket Service Initialized....");
    }

    // Setup the specialized admin notification listener
    this.setupAdminListener();

    // Reactive connection management based on auth state
    effect(() => {
      const member = this.authService.member();
      if (member?._id || member?.id) {
        this.connect();
      } else {
        this.disconnect();
      }
    });
  }

  /**
   * Manually connects to the socket server.
   */
  connect() {
    if (this.socket) {
      if (!environment.production) {
        console.log("....socket connecting....");
      }
      // Token query param removed in favor of cookies
      // this.socket.io.opts.query = ...

      if (!this.socket.connected) {
        this.socket.connect();
      }
    }
  }

  /**
   * Disconnects from the socket server.
   */
  disconnect() {
    if (this.socket && this.socket.connected) {
      this.socket.disconnect();
    }
  }

  /**
   * Setup consolidated admin notification listener
   */
  private setupAdminListener() {
    const event = 'admin:notification';
    this.socket.on(event, (data: any) => {
      if (!environment.production) {
        console.log(`Received WebSocket event [${event}]`); // payload data removed for security
      }
      this.notificationSubject.next({ event, data });
    });

    this.socket.on('connect', () => {
      if (!environment.production) {
        console.log('Successfully connected to WebSocket server');
      }
      // Automatically join the admins room to receive system notifications
      this.socket.emit('join-admin-room');
    });

    this.socket.on('error', (error) => {
      if (!environment.production) {
        console.error('Socket error:', error);
      }
    });
  }

  /**
   * Listen for events from the socket server.
   */
  onEvent<T>(eventName: string): Observable<T> {
    return new Observable((subscriber) => {
      if (!this.socket) {
        return;
      }

      this.socket.on(eventName, (data: T) => {
        subscriber.next(data);
      });

      return () => {
        this.socket.off(eventName);
      };
    });
  }

  /**
   * Emit an event to the server
   */
  emit(event: string, data: any) {
    if (this.socket) {
      this.socket.emit(event, data);
    }
  }
}
